<template>
  <div
    class="group bg-white dark:bg-black rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 dark:border-gray-700 hover:border-opacity-0 transform hover:-translate-y-2 flex flex-col h-full"
    :style="{
      borderColor: brandColors.primary + '60',
      '--brand-color': brandColors.primary
    }"
  >
    <!-- Card Header with Gradient Background -->
    <div
      class="relative p-4 sm:p-5 lg:p-6 pb-4"
      :style="{ background: `linear-gradient(135deg, ${brandColors.primary}12 0%, ${brandColors.primary}05 100%)` }"
    >
      <!-- Top Row: Car Icon and Year Badge -->
      <div class="flex items-start justify-between mb-3 sm:mb-4">
        <!-- Car Icon with Animation -->
        <div
          class="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0"
          :style="{ backgroundColor: brandColors.primary + '15' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 sm:h-7 sm:w-7"
            :style="{ color: brandColors.primary }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
            <circle cx="7" cy="17" r="2"/>
            <path d="M9 17h6"/>
            <circle cx="17" cy="17" r="2"/>
          </svg>
        </div>

        <!-- Year Badge -->
        <div
          class="px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold shadow-sm flex-shrink-0"
          :style="{
            backgroundColor: brandColors.primary,
            color: 'white'
          }"
        >
          {{ vehicle.vehicle_year }}
        </div>
      </div>

      <!-- Vehicle Name -->
      <h3 class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 leading-tight break-words">
        {{ vehicle.vehicle_make }}
        <span class="block text-base sm:text-lg font-semibold text-gray-600 dark:text-gray-300 mt-0.5">
          {{ vehicle.vehicle_model }}
        </span>
      </h3>

      <!-- Plate Number with Styled Badge -->
      <div class="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white dark:bg-black rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm max-w-full">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <rect x="3" y="6" width="18" height="12" rx="2" ry="2"/>
          <path d="M7 10h10M7 14h6"/>
        </svg>
        <span class="font-mono font-bold text-sm sm:text-base text-gray-900 dark:text-white tracking-wider truncate uppercase">
          {{ vehicle.plate_number }}
        </span>
      </div>
    </div>

    <!-- Tire Count Section -->
    <div class="px-4 sm:px-5 lg:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-y border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2 sm:gap-3">
        <!-- Tire Icon -->
        <div
          class="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0"
          :style="{ backgroundColor: brandColors.primary + '20' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 sm:h-5 sm:w-5"
            :style="{ color: brandColors.primary }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="12" r="6"/>
            <circle cx="12" cy="12" r="2"/>
          </svg>
        </div>

        <!-- Tire Count Text -->
        <div class="flex-1 min-w-0">
          <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Registered Tires</p>
          <p class="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
            {{ vehicle.tires_count || 0 }}
            <span class="text-xs sm:text-sm font-normal text-gray-600 dark:text-gray-400">
              {{ vehicle.tires_count === 1 ? 'tire' : 'tires' }}
            </span>
          </p>
        </div>
      </div>
    </div>

    <!-- Card Actions -->
    <div class="px-4 sm:px-5 lg:px-6 py-3 sm:py-4 bg-white dark:bg-black mt-auto">
      <!-- Desktop: 3 buttons in a row -->
      <div class="hidden lg:flex gap-1.5 2xl:gap-1">
        <button
          @click="$emit('view', vehicle)"
          class="flex-1 inline-flex items-center justify-center gap-1.5 2xl:gap-1 px-2.5 2xl:px-2 py-2.5 2xl:py-2 text-sm 2xl:text-xs font-semibold text-white rounded-xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
          :style="{ backgroundColor: brandColors.primary }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 2xl:h-3.5 2xl:w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <span class="truncate">View</span>
        </button>
        <button
          @click="$emit('edit', vehicle)"
          class="flex-1 inline-flex items-center justify-center gap-1.5 2xl:gap-1 px-2.5 2xl:px-2 py-2.5 2xl:py-2 text-sm 2xl:text-xs font-semibold rounded-xl transition-all duration-200 border-2 hover:shadow-md active:scale-95"
          :style="{
            color: brandColors.primary,
            borderColor: brandColors.primary + '40',
            backgroundColor: 'transparent'
          }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 2xl:h-3.5 2xl:w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
            <path d="m15 5 4 4"/>
          </svg>
          <span class="truncate">Edit</span>
        </button>
        <button
          @click="$emit('delete', vehicle)"
          class="flex-1 inline-flex items-center justify-center gap-1.5 2xl:gap-1 px-2.5 2xl:px-2 py-2.5 2xl:py-2 text-sm 2xl:text-xs font-semibold text-red-600 dark:text-red-400 rounded-xl transition-all duration-200 border-2 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:shadow-md active:scale-95"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 2xl:h-3.5 2xl:w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18"/>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
          </svg>
          <span class="truncate">Delete</span>
        </button>
      </div>

      <!-- Tablet & Mobile: Stacked layout for better readability -->
      <div class="lg:hidden space-y-2">
        <button
          @click="$emit('view', vehicle)"
          class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
          :style="{ backgroundColor: brandColors.primary }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <span>View Details</span>
        </button>
        <div class="flex gap-2">
          <button
            @click="$emit('edit', vehicle)"
            class="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 border-2 hover:shadow-md active:scale-95"
            :style="{
              color: brandColors.primary,
              borderColor: brandColors.primary + '40',
              backgroundColor: 'transparent'
            }"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
              <path d="m15 5 4 4"/>
            </svg>
            <span>Edit</span>
          </button>
          <button
            @click="$emit('delete', vehicle)"
            class="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400 rounded-xl transition-all duration-200 border-2 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:shadow-md active:scale-95"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18"/>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Vehicle } from '@/types/vehicle';
import { useBrand } from '@/composables/brand/useBrand';

defineProps<{
  vehicle: Vehicle;
}>();

defineEmits<{
  view: [vehicle: Vehicle];
  edit: [vehicle: Vehicle];
  delete: [vehicle: Vehicle];
}>();

const { brandColors } = useBrand();
</script>

